import React, { useCallback } from "react";
import Slider, { SliderProps, SliderThumb } from "@mui/material/Slider";
import { styled } from "@mui/material/styles";
import { EmtTheme } from "../types";

export type EmtDistanceProps = Omit<SliderProps, "onChange"> & {
  nonce?: string;
  track?: "inverted" | "normal";
  onChange?: (value: number | number[], e?: Event) => void | Promise<void>;
  minDistanceBetweenThumbs?: number;
};

const EmtSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== "color",
})(
  ({
    theme,
    color,
    track,
  }: {
    theme?: EmtTheme;
    color:
      | "error"
      | "primary"
      | "secondary"
      | "info"
      | "success"
      | "warning"
      | "purple";
    track: "inverted" | "normal";
  }) => ({
    color: theme?.palette[color]?.main || theme?.palette.primary.main,
    height: 4,
    padding: "13px 0",
    touchAction: "none",
    WebkitTapHighlightColor: "transparent",
    "& .MuiSlider-thumb": {
      touchAction: "none",
      height: 27,
      width: 27,
      backgroundColor: theme?.palette.common.white,
      border: "1px solid currentColor",
      "&:hover": {
        boxShadow: "0 0 0 8px transparent !important",
      },
      "& .button-bar": {
        height: 12,
        width: 1,
        backgroundColor: theme?.palette.greyGradient[150],
        marginLeft: 1.6,
        marginRight: 1.6,
      },
    },
    // Conditional styling for inverted or normal track
    "& .MuiSlider-track": {
      height: track === "inverted" ? 6 : 3,
      ...(track === "inverted" && { border: "none!important" }),
      backgroundColor:
        track === "inverted"
          ? theme?.palette.greyGradient[150] // Inverted track style
          : theme?.palette[color]?.main, // Normal track style
    },
    "& .MuiSlider-rail": {
      height: track === "inverted" ? 6 : 3,
      ...(track === "inverted" && { border: "none!important" }),
      backgroundColor:
        track === "inverted"
          ? theme?.palette.primary.main // Rail becomes the main color in inverted mode
          : theme?.palette.greyGradient[150], // Normal mode rail
    },
    "& .MuiSlider-mark ": {
      backgroundColor: "transparent",
    },
    "& .MuiSlider-valueLabel": {
      background: theme?.palette.greyGradient.main,
      borderRadius: "10px",
      "&::before": {
        bottom: "1px",
      },
    },
  })
);

// @ts-ignore
const EmtSliderThumb = styled(SliderThumb, {
  shouldForwardProp: (prop) => prop !== "color",
})(
  ({
    theme,
    color,
  }: {
    theme?: EmtTheme;
    color:
      | "error"
      | "primary"
      | "secondary"
      | "info"
      | "success"
      | "warning"
      | "purple";
  }) => ({
    borderRadius: "10px",
    border: `2px solid ${
      theme?.palette[color]?.main || theme?.palette.primary.main
    } !important`,
    "&.MuiSlider-thumb": {
      width: "28px",
      height: "28px",
    },
  })
);

const ButtonThumb = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<unknown> & { color?: string }
>(({ children, color, ...rest }, ref) => (
  // @ts-ignore
  <EmtSliderThumb {...rest} ref={ref} color={color}>
    {children}
    <span className="button-bar" />
    <span className="button-bar" />
    <span className="button-bar" />
  </EmtSliderThumb>
));
ButtonThumb.displayName = "ButtonThumb";

const defaultMarks = [
  {
    value: 0,
    label: "0 km",
  },
  {
    value: 50,
    label: "50 km",
  },
];

export function EmtDistance(props: EmtDistanceProps) {
  const {
    color = "primary",
    marks = defaultMarks,
    max = 50,
    min,
    defaultValue = [0, 20],
    valueLabelDisplay = "on",
    valueLabelFormat = (v) => `${v} km`,
    track = "normal", // Default to "normal"
    onChange,
    value,
    minDistanceBetweenThumbs = 5,
    ...rest
  } = props;

  const internalOnChange = useCallback(
    (e: Event, newValue: number | number[], activeThumb: number) => {
      if (!onChange) return;

      if (typeof newValue === "number") {
        onChange(newValue, e);
        return;
      }

      if (newValue[1] - newValue[0] < minDistanceBetweenThumbs) {
        if (activeThumb === 0) {
          const clamped = Math.min(newValue[0], max - minDistanceBetweenThumbs);
          onChange([clamped, clamped + minDistanceBetweenThumbs], e);
        } else {
          const clamped = Math.max(newValue[1], minDistanceBetweenThumbs);
          onChange([clamped - minDistanceBetweenThumbs, clamped], e);
        }
      } else {
        onChange(newValue, e);
      }
    },
    [onChange, minDistanceBetweenThumbs, max]
  );

  return (
    <EmtSlider
      color={color}
      value={value}
      onChange={(e, value, activeThumb) =>
        internalOnChange(e, value, activeThumb)
      }
      slots={{ thumb: ButtonThumb }}
      slotProps={{
        thumb: { color: color || "primary" } as any,
      }}
      max={max}
      track={track} // Pass track prop
      {...(min !== undefined ? { min } : {})}
      {...(!value ? { defaultValue } : {})}
      valueLabelDisplay={valueLabelDisplay}
      marks={marks}
      valueLabelFormat={valueLabelFormat}
      {...rest}
    />
  );
}
